import {
  SCRIPT_REFERENCE_VIDEO_URL_MAX,
  isFullHttpUrl,
  type OrderScriptReference,
} from "@nexus/contracts"
import * as React from "react"

export type ScriptReferenceError = "empty" | "tooLong" | "invalid"

export interface ScriptReferenceDrafts {
  /** Item ids whose reference row is open, in the order the user opened them. */
  openIds: string[]
  urlFor: (itemId: string) => string
  errorFor: (itemId: string) => ScriptReferenceError | null
  showErrors: boolean
  /**
   * Whether an open row among these items is incomplete. Rows for items that
   * are not on the current send (a partner already has them) never block it.
   */
  hasErrorsFor: (itemIds: readonly string[]) => boolean
  open: (itemId: string) => void
  remove: (itemId: string) => void
  setUrl: (itemId: string, url: string) => void
  /** Turns on the error display and reports whether every open row on this send is complete. */
  validate: (itemIds: readonly string[]) => boolean
  /** The references for the items that go out on one send. */
  payloadFor: (itemIds: readonly string[]) => OrderScriptReference[]
}

/** Spread into a send body; adds nothing when no item on the send has a link. */
export function scriptReferencesPayload(
  drafts: ScriptReferenceDrafts,
  itemIds: readonly string[],
): { scriptReferences?: OrderScriptReference[] } {
  const scriptReferences = drafts.payloadFor(itemIds)
  return scriptReferences.length > 0 ? { scriptReferences } : {}
}

/** Same rules as the API's `ScriptReferenceVideoUrlSchema`, split by what the user must fix. */
export function referenceUrlError(url: string): ScriptReferenceError | null {
  const trimmed = url.trim()
  if (trimmed.length === 0) return "empty"
  if (trimmed.length > SCRIPT_REFERENCE_VIDEO_URL_MAX) return "tooLong"
  return isFullHttpUrl(trimmed) ? null : "invalid"
}

/**
 * Per-item reference links for one send. Cleared each time the dialog
 * opens; kept while it stays open so a retried send carries the same links.
 */
export function useScriptReferenceDrafts(open: boolean): ScriptReferenceDrafts {
  const [openIds, setOpenIds] = React.useState<string[]>([])
  const [urls, setUrls] = React.useState<Record<string, string>>({})
  const [showErrors, setShowErrors] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setOpenIds([])
    setUrls({})
    setShowErrors(false)
  }, [open])

  const urlFor = (itemId: string) => urls[itemId] ?? ""
  const errorFor = (itemId: string) =>
    openIds.includes(itemId) ? referenceUrlError(urlFor(itemId)) : null
  const hasErrorsFor = (itemIds: readonly string[]) =>
    openIds.some((itemId) => itemIds.includes(itemId) && errorFor(itemId) !== null)

  return {
    openIds,
    urlFor,
    errorFor,
    showErrors,
    hasErrorsFor,
    open: (itemId) =>
      setOpenIds((current) => (current.includes(itemId) ? current : [...current, itemId])),
    remove: (itemId) => {
      setOpenIds((current) => current.filter((id) => id !== itemId))
      setUrls((current) => {
        const { [itemId]: _removed, ...rest } = current
        return rest
      })
    },
    setUrl: (itemId, url) => setUrls((current) => ({ ...current, [itemId]: url })),
    validate: (itemIds) => {
      setShowErrors(true)
      return !hasErrorsFor(itemIds)
    },
    payloadFor: (itemIds) =>
      openIds
        .filter((itemId) => itemIds.includes(itemId) && errorFor(itemId) === null)
        .map((itemId) => ({ itemId, url: urlFor(itemId).trim() })),
  }
}
